import type { Citation } from "./types";

export interface TranscriptParagraphLike {
  start: number;
  end: number;
  sentences?: Array<{ text?: string }>;
  [key: string]: unknown;
}

export interface CitationTarget {
  /** Paragraph index in the transcript, or -1 when no transcript is available. */
  index: number;
  /** Seconds to seek the video to. */
  seconds: number;
}

/**
 * Resolve which transcript paragraph (and video second) a citation points to.
 * Prefers the paragraph containing `startSeconds`; falls back to a `citedText`
 * substring match, then to the nearest paragraph by start time.
 */
export function resolveCitationTarget(
  citation: Pick<Citation, "startSeconds" | "citedText">,
  paragraphs: TranscriptParagraphLike[] | null | undefined,
): CitationTarget {
  if (!paragraphs || paragraphs.length === 0) {
    return { index: -1, seconds: citation.startSeconds };
  }

  const bySeconds = paragraphs.findIndex(
    (p) => citation.startSeconds >= p.start && citation.startSeconds < p.end,
  );
  if (bySeconds !== -1) {
    return { index: bySeconds, seconds: paragraphs[bySeconds].start };
  }

  if (citation.citedText) {
    const needle = citation.citedText.toLowerCase().slice(0, 40);
    const byText = paragraphs.findIndex((p) =>
      (p.sentences ?? []).some((s) =>
        (s.text ?? "").toLowerCase().includes(needle),
      ),
    );
    if (byText !== -1) {
      return { index: byText, seconds: paragraphs[byText].start };
    }
  }

  let nearest = 0;
  let best = Number.POSITIVE_INFINITY;
  paragraphs.forEach((p, i) => {
    const distance = Math.abs(p.start - citation.startSeconds);
    if (distance < best) {
      best = distance;
      nearest = i;
    }
  });
  return { index: nearest, seconds: paragraphs[nearest].start };
}
